import type { TarotCardName } from "@/domain/cards";
import { z } from "zod/v4";

type Card = {
	name: TarotCardName;
	suit: "major" | "wands" | "cups" | "swords" | "pentacles";
};

export const blueprint: Card[] = [
	{ suit: "major", name: "00-fool" },
	{ suit: "major", name: "01-magician" },
	{ suit: "major", name: "02-high-priestess" },
	{ suit: "major", name: "03-empress" },
	{ suit: "major", name: "04-emperor" },
	{ suit: "major", name: "05-hierophant" },
	{ suit: "major", name: "06-lovers" },
	{ suit: "major", name: "07-chariot" },
	{ suit: "major", name: "08-strength" },
	{ suit: "major", name: "09-hermit" },
	{ suit: "major", name: "10-wheel-of-fortune" },
	{ suit: "major", name: "11-justice" },
	{ suit: "major", name: "12-hanged-man" },
	{ suit: "major", name: "13-death" },
	{ suit: "major", name: "14-temperance" },
	{ suit: "major", name: "15-devil" },
	{ suit: "major", name: "16-tower" },
	{ suit: "major", name: "17-star" },
	{ suit: "major", name: "18-moon" },
	{ suit: "major", name: "19-sun" },
	{ suit: "major", name: "20-judgement" },
	{ suit: "major", name: "21-world" },
	{ suit: "cups", name: "cups-ace" },
	{ suit: "cups", name: "cups-02" },
	{ suit: "cups", name: "cups-03" },
	{ suit: "cups", name: "cups-04" },
	{ suit: "cups", name: "cups-05" },
	{ suit: "cups", name: "cups-06" },
	{ suit: "cups", name: "cups-07" },
	{ suit: "cups", name: "cups-08" },
	{ suit: "cups", name: "cups-09" },
	{ suit: "cups", name: "cups-10" },
	{ suit: "cups", name: "cups-page" },
	{ suit: "cups", name: "cups-knight" },
	{ suit: "cups", name: "cups-queen" },
	{ suit: "cups", name: "cups-king" },
	{ suit: "pentacles", name: "pentacles-ace" },
	{ suit: "pentacles", name: "pentacles-02" },
	{ suit: "pentacles", name: "pentacles-03" },
	{ suit: "pentacles", name: "pentacles-04" },
	{ suit: "pentacles", name: "pentacles-05" },
	{ suit: "pentacles", name: "pentacles-06" },
	{ suit: "pentacles", name: "pentacles-07" },
	{ suit: "pentacles", name: "pentacles-08" },
	{ suit: "pentacles", name: "pentacles-09" },
	{ suit: "pentacles", name: "pentacles-10" },
	{ suit: "pentacles", name: "pentacles-page" },
	{ suit: "pentacles", name: "pentacles-knight" },
	{ suit: "pentacles", name: "pentacles-queen" },
	{ suit: "pentacles", name: "pentacles-king" },
	{ suit: "swords", name: "swords-ace" },
	{ suit: "swords", name: "swords-02" },
	{ suit: "swords", name: "swords-03" },
	{ suit: "swords", name: "swords-04" },
	{ suit: "swords", name: "swords-05" },
	{ suit: "swords", name: "swords-06" },
	{ suit: "swords", name: "swords-07" },
	{ suit: "swords", name: "swords-08" },
	{ suit: "swords", name: "swords-09" },
	{ suit: "swords", name: "swords-10" },
	{ suit: "swords", name: "swords-page" },
	{ suit: "swords", name: "swords-knight" },
	{ suit: "swords", name: "swords-queen" },
	{ suit: "swords", name: "swords-king" },
	{ suit: "wands", name: "wands-ace" },
	{ suit: "wands", name: "wands-02" },
	{ suit: "wands", name: "wands-03" },
	{ suit: "wands", name: "wands-04" },
	{ suit: "wands", name: "wands-05" },
	{ suit: "wands", name: "wands-06" },
	{ suit: "wands", name: "wands-07" },
	{ suit: "wands", name: "wands-08" },
	{ suit: "wands", name: "wands-09" },
	{ suit: "wands", name: "wands-10" },
	{ suit: "wands", name: "wands-page" },
	{ suit: "wands", name: "wands-knight" },
	{ suit: "wands", name: "wands-queen" },
	{ suit: "wands", name: "wands-king" },
];

export const localizedCards: Record<string, { en: string; ru: string }> = {
	"00-fool": { en: "The Fool", ru: "Шут" },
	"01-magician": { en: "The Magician", ru: "Маг" },
	"02-high-priestess": { en: "The High Priestess", ru: "Верховная Жрица" },
	"03-empress": { en: "The Empress", ru: "Императрица" },
	"04-emperor": { en: "The Emperor", ru: "Император" },
	"05-hierophant": { en: "The Hierophant", ru: "Верховный Жрец" },
	"06-lovers": { en: "The Lovers", ru: "Влюбленные" },
	"07-chariot": { en: "The Chariot", ru: "Колесница" },
	"08-strength": { en: "Strength", ru: "Сила" },
	"09-hermit": { en: "The Hermit", ru: "Отшельник" },
	"10-wheel-of-fortune": { en: "Wheel of Fortune", ru: "Колесо Фортуны" },
	"11-justice": { en: "Justice", ru: "Справедливость" },
	"12-hanged-man": { en: "The Hanged Man", ru: "Повешенный" },
	"13-death": { en: "Death", ru: "Смерть" },
	"14-temperance": { en: "Temperance", ru: "Умеренность" },
	"15-devil": { en: "The Devil", ru: "Дьявол" },
	"16-tower": { en: "The Tower", ru: "Башня" },
	"17-star": { en: "The Star", ru: "Звезда" },
	"18-moon": { en: "The Moon", ru: "Луна" },
	"19-sun": { en: "The Sun", ru: "Солнце" },
	"20-judgement": { en: "Judgement", ru: "Суд" },
	"21-world": { en: "The World", ru: "Мир" },

	"cups-ace": { en: "Ace of Cups", ru: "Туз Чаш" },
	"cups-02": { en: "Two of Cups", ru: "Двойка Чаш" },
	"cups-03": { en: "Three of Cups", ru: "Тройка Чаш" },
	"cups-04": { en: "Four of Cups", ru: "Четверка Чаш" },
	"cups-05": { en: "Five of Cups", ru: "Пятерка Чаш" },
	"cups-06": { en: "Six of Cups", ru: "Шестерка Чаш" },
	"cups-07": { en: "Seven of Cups", ru: "Семерка Чаш" },
	"cups-08": { en: "Eight of Cups", ru: "Восьмерка Чаш" },
	"cups-09": { en: "Nine of Cups", ru: "Девятка Чаш" },
	"cups-10": { en: "Ten of Cups", ru: "Десятка Чаш" },
	"cups-page": { en: "Page of Cups", ru: "Паж Чаш" },
	"cups-knight": { en: "Knight of Cups", ru: "Рыцарь Чаш" },
	"cups-queen": { en: "Queen of Cups", ru: "Королева Чаш" },
	"cups-king": { en: "King of Cups", ru: "Король Чаш" },

	"pentacles-ace": { en: "Ace of Pentacles", ru: "Туз Пентаклей" },
	"pentacles-02": { en: "Two of Pentacles", ru: "Двойка Пентаклей" },
	"pentacles-03": { en: "Three of Pentacles", ru: "Тройка Пентаклей" },
	"pentacles-04": { en: "Four of Pentacles", ru: "Четверка Пентаклей" },
	"pentacles-05": { en: "Five of Pentacles", ru: "Пятерка Пентаклей" },
	"pentacles-06": { en: "Six of Pentacles", ru: "Шестерка Пентаклей" },
	"pentacles-07": { en: "Seven of Pentacles", ru: "Семерка Пентаклей" },
	"pentacles-08": { en: "Eight of Pentacles", ru: "Восьмерка Пентаклей" },
	"pentacles-09": { en: "Nine of Pentacles", ru: "Девятка Пентаклей" },
	"pentacles-10": { en: "Ten of Pentacles", ru: "Десятка Пентаклей" },
	"pentacles-page": { en: "Page of Pentacles", ru: "Паж Пентаклей" },
	"pentacles-knight": { en: "Knight of Pentacles", ru: "Рыцарь Пентаклей" },
	"pentacles-queen": { en: "Queen of Pentacles", ru: "Королева Пентаклей" },
	"pentacles-king": { en: "King of Pentacles", ru: "Король Пентаклей" },

	"swords-ace": { en: "Ace of Swords", ru: "Туз Мечей" },
	"swords-02": { en: "Two of Swords", ru: "Двойка Мечей" },
	"swords-03": { en: "Three of Swords", ru: "Тройка Мечей" },
	"swords-04": { en: "Four of Swords", ru: "Четверка Мечей" },
	"swords-05": { en: "Five of Swords", ru: "Пятерка Мечей" },
	"swords-06": { en: "Six of Swords", ru: "Шестерка Мечей" },
	"swords-07": { en: "Seven of Swords", ru: "Семерка Мечей" },
	"swords-08": { en: "Eight of Swords", ru: "Восьмерка Мечей" },
	"swords-09": { en: "Nine of Swords", ru: "Девятка Мечей" },
	"swords-10": { en: "Ten of Swords", ru: "Десятка Мечей" },
	"swords-page": { en: "Page of Swords", ru: "Паж Мечей" },
	"swords-knight": { en: "Knight of Swords", ru: "Рыцарь Мечей" },
	"swords-queen": { en: "Queen of Swords", ru: "Королева Мечей" },
	"swords-king": { en: "King of Swords", ru: "Король Мечей" },

	"wands-ace": { en: "Ace of Wands", ru: "Туз Жезлов" },
	"wands-02": { en: "Two of Wands", ru: "Двойка Жезлов" },
	"wands-03": { en: "Three of Wands", ru: "Тройка Жезлов" },
	"wands-04": { en: "Four of Wands", ru: "Четверка Жезлов" },
	"wands-05": { en: "Five of Wands", ru: "Пятерка Жезлов" },
	"wands-06": { en: "Six of Wands", ru: "Шестерка Жезлов" },
	"wands-07": { en: "Seven of Wands", ru: "Семерка Жезлов" },
	"wands-08": { en: "Eight of Wands", ru: "Восьмерка Жезлов" },
	"wands-09": { en: "Nine of Wands", ru: "Девятка Жезлов" },
	"wands-10": { en: "Ten of Wands", ru: "Десятка Жезлов" },
	"wands-page": { en: "Page of Wands", ru: "Паж Жезлов" },
	"wands-knight": { en: "Knight of Wands", ru: "Рыцарь Жезлов" },
	"wands-queen": { en: "Queen of Wands", ru: "Королева Жезлов" },
	"wands-king": { en: "King of Wands", ru: "Король Жезлов" },
};

export const CardOfDaySchema = z.object({
	date: z.string().length(10),
	name: z.string().min(1),
	isUpright: z.boolean(),
});

export function parseCod(lastCodCardRaw?: string) {
	if (!lastCodCardRaw) return null;

	const [date, name, isUpright] = lastCodCardRaw.split("::");

	const result = CardOfDaySchema.safeParse({
		date: date,
		name: name,
		isUpright: isUpright === "true",
	});

	if (!result.success) return null;

	return result.data;
}
